import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, Settings2, X, Check } from "lucide-react";
import { DEFAULT_CONSENT, getStoredConsent, persistConsent } from "@/lib/analytics";

// Three-button cookie banner with optional granular toggles, wired to
// Google Consent Mode v2 via persistConsent(). All categories default to
// DENIED until the user makes a choice — meaning no GTM tag that depends
// on analytics_storage / ad_storage will fire on first paint.

const CATEGORIES = [
  {
    key: "analytics_storage",
    label: "Analytics",
    description: "Helps us understand how visitors use the site (anonymous traffic data).",
  },
  {
    key: "ad_storage",
    label: "Advertising",
    description: "Used by Meta, Google Ads and TikTok to measure campaign performance.",
  },
  {
    key: "ad_user_data",
    label: "Ad user data",
    description: "Sharing of conversion data with ad platforms (linked to Advertising).",
  },
  {
    key: "ad_personalization",
    label: "Personalisation",
    description: "Allows tailored advertising based on your interaction with USDD content.",
  },
];

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [draft, setDraft] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    // Show only when a decision hasn't been made yet.
    const stored = getStoredConsent();
    if (!stored?.decided_at) setVisible(true);
  }, []);

  const decide = (consent) => {
    persistConsent(consent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="cookie-banner"
        className="fixed inset-x-2 sm:inset-x-4 bottom-2 sm:bottom-4 z-[80] no-print"
      >
        <div className="max-w-5xl mx-auto bg-black/95 backdrop-blur-md border border-brand-yellow/30 rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
          {!customising ? (
            <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
              <div className="flex items-start gap-3 lg:flex-1">
                <div className="w-10 h-10 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center flex-shrink-0">
                  <Cookie size={18} className="text-brand-yellow" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow font-black mb-1">Cookies &amp; tracking</div>
                  <p className="text-sm text-zinc-200 leading-relaxed max-w-2xl">
                    We use a small set of cookies for analytics and advertising — to measure what's working and improve the experience.
                    Essential cookies for the site to function are always on.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
                <button
                  type="button"
                  onClick={() => decide({
                    analytics_storage: "denied",
                    ad_storage: "denied",
                    ad_user_data: "denied",
                    ad_personalization: "denied",
                    functionality_storage: "granted",
                    security_storage: "granted",
                  })}
                  data-testid="cookie-reject-btn"
                  className="px-4 py-2.5 rounded-full border border-white/15 text-zinc-200 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] hover:border-white/40 hover:text-white transition-all"
                >
                  Reject non-essential
                </button>
                <button
                  type="button"
                  onClick={() => { setDraft(getStoredConsent() || DEFAULT_CONSENT); setCustomising(true); }}
                  data-testid="cookie-customise-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-white/15 text-zinc-200 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
                >
                  <Settings2 size={11} /> Customise
                </button>
                <button
                  type="button"
                  onClick={() => decide({
                    analytics_storage: "granted",
                    ad_storage: "granted",
                    ad_user_data: "granted",
                    ad_personalization: "granted",
                    functionality_storage: "granted",
                    security_storage: "granted",
                  })}
                  data-testid="cookie-accept-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-yellow text-black text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover transition-all glow-yellow"
                >
                  <Check size={12} /> Accept all
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck size={18} className="text-brand-yellow" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow font-black mb-1">Customise tracking</div>
                    <p className="text-sm text-zinc-300 max-w-2xl">
                      Switch individual categories on or off. We use Google's Consent Mode v2 so your choice applies to every tracker — GA, Meta, TikTok, Google Ads.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setCustomising(false)}
                  aria-label="Close customise"
                  className="text-zinc-500 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-2 mb-5">
                {CATEGORIES.map((cat) => {
                  const granted = draft[cat.key] === "granted";
                  return (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => setDraft({ ...draft, [cat.key]: granted ? "denied" : "granted" })}
                      data-testid={`cookie-toggle-${cat.key}`}
                      className={`w-full flex items-center gap-3 p-3 sm:p-4 rounded-xl border text-left transition-all ${granted ? "border-brand-yellow/40 bg-brand-yellow/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
                    >
                      <div className={`w-9 h-5 rounded-full p-0.5 flex-shrink-0 transition-colors ${granted ? "bg-brand-yellow" : "bg-white/20"}`}>
                        <div className={`w-4 h-4 rounded-full bg-black transition-transform ${granted ? "translate-x-4" : ""}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white">{cat.label}</div>
                        <div className="text-[11px] text-zinc-400 leading-snug mt-0.5">{cat.description}</div>
                      </div>
                    </button>
                  );
                })}
                <div className="p-3 sm:p-4 rounded-xl border border-white/10 bg-white/[0.02] flex items-center gap-3">
                  <div className="w-9 h-5 rounded-full p-0.5 flex-shrink-0 bg-brand-yellow opacity-50">
                    <div className="w-4 h-4 rounded-full bg-black translate-x-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-zinc-300">Essential (always on)</div>
                    <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">Required for the site, checkout flow and security to work.</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setCustomising(false)}
                  className="px-4 py-2.5 rounded-full border border-white/15 text-zinc-300 text-[11px] font-bold uppercase tracking-[0.2em] hover:border-white/40 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => decide(draft)}
                  data-testid="cookie-save-btn"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-yellow text-black text-[11px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover transition-all glow-yellow"
                >
                  <Check size={12} /> Save preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
